import { Building2, FileText, GraduationCap } from 'lucide-react';

interface NoticeStatCardsProps {
  totalCount: number;
  instituteCount?: number;
  classCount?: number;
  activeSource?: string;
  onSelectSource?: (source: 'ALL' | 'INSTITUTE' | 'CLASS') => void;
  /** Preserved props for backward compatibility */
  generalCount?: number;
  eventsCount?: number;
  importantCount?: number;
  activeFilter?: string;
  onSelectFilter?: (type: 'ALL' | 'GENERAL' | 'EVENT' | 'IMPORTANT') => void;
}

// ============================================================================
// Commented out (General, Event, Important) stat cards as requested:
// We have only general notices, so the cards below are commented out in favor of
// Institute Notices and Class Notices count cards.
// ============================================================================
//
// 2. General Notices
// <div
//   onClick={() => onSelectFilter?.('GENERAL')}
//   className={`rounded-2xl px-4 py-2 shadow-xs transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer bg-emerald-50/90 dark:bg-slate-900 border ${
//     activeFilter === 'GENERAL'
//       ? 'border-emerald-500 ring-1 ring-inset ring-emerald-500/30'
//       : 'border-emerald-200/90 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-slate-700'
//   }`}
// >
//   <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-emerald-500/15 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
//     <Megaphone className="h-5 w-5 sm:h-6 sm:w-6" />
//   </div>
//   <div className="flex flex-col min-w-0 flex-1">
//     <span className="text-xs sm:text-base font-semibold text-emerald-800 dark:text-emerald-400">General Notices</span>
//     <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-tight mt-0.5">
//       {generalCount}
//     </span>
//   </div>
// </div>
//
// 3. Events
// <div
//   onClick={() => onSelectFilter?.('EVENT')}
//   className={`rounded-2xl px-4 py-2 shadow-xs transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer bg-rose-50/90 dark:bg-slate-900 border ${
//     activeFilter === 'EVENT'
//       ? 'border-rose-500 ring-1 ring-inset ring-rose-500/30'
//       : 'border-rose-200/90 dark:border-slate-800 hover:border-rose-300 dark:hover:border-slate-700'
//   }`}
// >
//   <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-rose-500/15 border border-rose-500/20 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0">
//     <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
//   </div>
//   <div className="flex flex-col min-w-0 flex-1">
//     <span className="text-xs sm:text-base font-semibold text-rose-800 dark:text-rose-400">Events</span>
//     <span className="text-2xl sm:text-3xl font-black text-rose-600 dark:text-rose-400 leading-tight mt-0.5">
//       {eventsCount}
//     </span>
//   </div>
// </div>
//
// 4. Important
// <div
//   onClick={() => onSelectFilter?.('IMPORTANT')}
//   className={`rounded-2xl px-4 py-2 shadow-xs transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer bg-amber-50/90 dark:bg-slate-900 border ${
//     activeFilter === 'IMPORTANT'
//       ? 'border-amber-500 ring-1 ring-inset ring-amber-500/30'
//       : 'border-amber-200/90 dark:border-slate-800 hover:border-amber-300 dark:hover:border-slate-700'
//   }`}
// >
//   <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-amber-500/15 border border-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
//     <Star className="h-5 w-5 sm:h-6 sm:w-6" />
//   </div>
//   <div className="flex flex-col min-w-0 flex-1">
//     <span className="text-xs sm:text-base font-semibold text-amber-800 dark:text-amber-400">Important</span>
//     <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 leading-tight mt-0.5">
//       {importantCount}
//     </span>
//   </div>
// </div>

export function NoticeStatCards({
  totalCount,
  instituteCount = 0,
  classCount = 0,
  activeSource = 'ALL',
  onSelectSource,
}: NoticeStatCardsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
      {/* 1. Total Notices */}
      <div
        onClick={() => onSelectSource?.('ALL')}
        className={`rounded-2xl px-4 py-2 shadow-xs transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer bg-indigo-50/90 dark:bg-slate-900 border ${
          activeSource === 'ALL'
            ? 'border-indigo-500 ring-1 ring-inset ring-indigo-500/30'
            : 'border-indigo-200/90 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-slate-700'
        }`}
      >
        <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-indigo-500/15 border border-indigo-500/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
          <FileText className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs sm:text-base font-semibold text-indigo-800 dark:text-indigo-400">Total Notices</span>
          <span className="text-2xl sm:text-3xl font-black text-indigo-600 dark:text-indigo-400 leading-tight mt-0.5">
            {totalCount}
          </span>
          <span className="text-xs text-indigo-600/90 dark:text-slate-400 font-medium mt-0.5">All Announcements</span>
        </div>
      </div>

      {/* 2. Institute Notices */}
      <div
        onClick={() => onSelectSource?.(activeSource === 'INSTITUTE' ? 'ALL' : 'INSTITUTE')}
        className={`rounded-2xl px-4 py-2 shadow-xs transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer bg-purple-50/90 dark:bg-slate-900 border ${
          activeSource === 'INSTITUTE'
            ? 'border-purple-500 ring-1 ring-inset ring-purple-500/30'
            : 'border-purple-200/90 dark:border-slate-800 hover:border-purple-300 dark:hover:border-slate-700'
        }`}
      >
        <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-purple-500/15 border border-purple-500/20 text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs sm:text-base font-semibold text-purple-800 dark:text-purple-400">Institute Notices</span>
          <span className="text-2xl sm:text-3xl font-black text-purple-600 dark:text-purple-400 leading-tight mt-0.5">
            {instituteCount}
          </span>
          <span className="text-xs text-purple-600/90 dark:text-slate-400 font-medium mt-0.5">From School Office</span>
        </div>
      </div>

      {/* 3. Class Notices */}
      <div
        onClick={() => onSelectSource?.(activeSource === 'CLASS' ? 'ALL' : 'CLASS')}
        className={`rounded-2xl px-4 py-2 shadow-xs transition-all flex items-center gap-3.5 sm:gap-4 cursor-pointer bg-emerald-50/90 dark:bg-slate-900 border ${
          activeSource === 'CLASS'
            ? 'border-emerald-500 ring-1 ring-inset ring-emerald-500/30'
            : 'border-emerald-200/90 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-slate-700'
        }`}
      >
        <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-xl bg-emerald-500/15 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
          <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs sm:text-base font-semibold text-emerald-800 dark:text-emerald-400">Class Notices</span>
          <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 leading-tight mt-0.5">
            {classCount}
          </span>
          <span className="text-xs text-emerald-600/90 dark:text-slate-400 font-medium mt-0.5">From Teachers</span>
        </div>
      </div>
    </div>
  );
}
